import { isMobileOnly } from 'react-device-detect'

import { isInvalidDate } from '@/utils/time'

/**
 * Shared session-storage slot for the WorkPeriodOverview and approval-view
 * filters. Both forms use this name so the selected user and month are cached
 * and stay in sync across the two views.
 *
 * `useForm`'s restore (retrieveFormState) discards the entire stored blob if it
 * finds any key the reading form doesn't declare, so both forms must build
 * their values from `workPeriodFilterDefaults` rather than listing keys inline.
 */
export const WORK_PERIOD_FILTER_STORAGE = 'work-period-shared-filters'

export const DEFAULT_WORK_PERIOD_VIEW_MODE: 'calendar' | 'list' = isMobileOnly
  ? 'list'
  : 'calendar'

export interface WorkPeriodFilterValues {
  user: string | null
  month: Date
  viewMode: 'calendar' | 'list'
  showResolved: boolean
  showPlannedWork: boolean
  showDayRest: boolean
  showWeekRest: boolean
  leaderGroup: string | null
  leaderGroupsUsers: string[]
  sumsCollapsed: boolean
  onlyApprovedSums: boolean
}

export const workPeriodFilterDefaults = (
  overrides: Partial<WorkPeriodFilterValues> = {}
): WorkPeriodFilterValues => ({
  user: null,
  month: new Date(),
  viewMode: DEFAULT_WORK_PERIOD_VIEW_MODE,
  showResolved: false,
  showPlannedWork: true,
  showDayRest: true,
  showWeekRest: true,
  leaderGroup: null,
  leaderGroupsUsers: [],
  sumsCollapsed: false,
  onlyApprovedSums: false,
  ...overrides,
})

const booleanOrDefault = (val: unknown) =>
  typeof val === 'boolean' ? val : undefined

export const workPeriodFilterTransforms = (canSeeOthers: boolean) => ({
  user: (val: unknown) => (val && canSeeOthers ? String(val) : null),
  month: (val: unknown) =>
    typeof val === 'string'
      ? isInvalidDate(new Date(val), new Date())
      : undefined,
  viewMode: (val: unknown) =>
    val === 'list' || val === 'calendar' ? val : undefined,
  sumsCollapsed: booleanOrDefault,
  onlyApprovedSums: booleanOrDefault,
  showDayRest: booleanOrDefault,
  showWeekRest: booleanOrDefault,
})
